import type {
  AvailabilityStatus,
  OfferBulkStatusRequest,
  OfferDuplicateDraft,
  OfferInput,
} from '@psp-aggregator/sdk';
import sdk from '@psp-aggregator/sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { OfferUpdateInput } from './offerDraft';

const invalidateOffers = (
  queryClient: ReturnType<typeof useQueryClient>,
): void => {
  void queryClient.invalidateQueries({ queryKey: ['listAdminOffers'] });
};

export const useCreateOfferMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: OfferInput) => sdk.requests.createOffer(payload),
    onSuccess: () => {
      invalidateOffers(queryClient);
    },
  });
};

export const useUpdateOfferMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      payload,
    }: {
      offerId: string;
      payload: OfferUpdateInput;
    }) => sdk.requests.updateOffer(payload as OfferInput, offerId),
    onSuccess: (_data, variables) => {
      invalidateOffers(queryClient);
      void queryClient.invalidateQueries({
        queryKey: ['getAdminOffer', variables.offerId],
      });
    },
  });
};

export const fetchOfferDuplicateDraft = (
  offerId: string,
): Promise<OfferDuplicateDraft> => sdk.requests.getOfferDuplicateDraft(offerId);

export const useBulkSetOfferStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: OfferBulkStatusRequest) =>
      sdk.requests.bulkSetOfferStatus(payload),
    onSuccess: () => {
      invalidateOffers(queryClient);
    },
  });
};

export const useDeleteOfferMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (offerId: string) => sdk.requests.deleteOffer(offerId),
    onSuccess: () => {
      invalidateOffers(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['listCatalogOffers'] });
    },
  });
};

export const useRestoreOfferMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (offerId: string) => sdk.requests.restoreOffer(offerId),
    onSuccess: (_data, offerId) => {
      invalidateOffers(queryClient);
      void queryClient.invalidateQueries({
        queryKey: ['getAdminOffer', offerId],
      });
      void queryClient.invalidateQueries({ queryKey: ['listCatalogOffers'] });
    },
  });
};

export const useSetOfferStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      status,
    }: {
      offerId: string;
      status: AvailabilityStatus;
    }) => sdk.requests.setOfferStatus({ status }, offerId),
    onSuccess: (_data, variables) => {
      invalidateOffers(queryClient);
      void queryClient.invalidateQueries({
        queryKey: ['getAdminOffer', variables.offerId],
      });
    },
  });
};
